<template>
   <div
    :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'"
    :class="[$i18n.locale === 'ar' ? 'text-right' : 'text-left']"
    class="flex flex-col min-h-screen"
  >

    <AppNavbar />
    
    <!-- Main Content takes available space -->
    <div class="flex-1">
      <router-view />
    </div>

    <FooterComponent />

    <!-- Floating Chat Button - Animated -->
  <button
  @click="handleChatClick"
  class="fixed bottom-6  bg-gradient-to-br from-teal-500 to-indigo-700 text-white p-4 rounded-full shadow-2xl hover:shadow-lg z-50 transition-all duration-300 hover:scale-105 group"
  :class="[
  { 'rotate-45': isChatOpen },
  $i18n.locale === 'ar' ? 'left-6' : 'right-6'
]"
>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-6 w-6 transform transition-transform duration-300 group-hover:scale-110"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          v-if="!isChatOpen"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
        />
        <path
          v-else
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
      <span class="sr-only">Chat</span>
      <!-- <div v-if="unreadCount > 0" class="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
        {{ unreadCount }}
      </div> -->
    </button>

    <!-- Chat Widget - Glass Morphism Style -->
    <div
      v-if="isChatOpen"
      class="max-size fixed bottom-24 right-6 w-full max-w-md h-[70vh] max-h-[600px] bg-white/90 backdrop-blur-lg rounded-2xl shadow-2xl flex flex-col z-50 overflow-hidden border border-white/20"
    >
      <!-- Header with Gradient -->
      <div
        class="bg-gradient-to-r from-teal-600 to-indigo-600 text-white p-4 flex justify-between items-center rounded-t-2xl"
      >
        <div class="flex items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
          <h3 class="font-bold text-lg">{{ $t("chat.chatWithSeller") }}</h3>
        </div>
        <div class="flex space-x-3">
          <button class="text-white/80 hover:text-white transition">
            <!-- <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v8a2 2 0 01-2 2h-2a2 2 0 01-2-2V6z"
              />
            </svg> -->
          </button>
          <button
            @click="isChatOpen = false"
            class="text-white/80 hover:text-white transition"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>

      <!-- Main Content -->
      <div class="flex flex-1 overflow-hidden">
        <!-- Users List -->
        <div
          class="w-1/3 border-r border-gray-200/50 bg-white/50 overflow-y-auto"
        >
          <div class="p-3 border-b border-gray-200/50">
            <div class="relative">
              <input
                type="text"
                v-model="searchQuery"
                :placeholder="$t('placeholders.searchContacts')"
                class="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-300/50 focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500/50 bg-white/70"
              />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 absolute left-3 top-2.5 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
          <div
            v-for="user in filteredUsers"
            :key="user.id"
            @click="selectUser(user)"
            class="p-3 border-b border-gray-200/50 cursor-pointer hover:bg-purple-50/50 transition flex items-center"
            :class="{ 'bg-purple-100/50': activeUser?.id === user.id }"
          >
            <div class="relative">
              <div
                class="displayNone h-10 w-10 rounded-full bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center text-purple-600 font-semibold mr-3"
              >
                {{ user.name.charAt(0) }}
              </div>
              <div
                v-if="user.isOnline"
                class="absolute bottom-0 right-2 h-3 w-3 bg-green-500 rounded-full border-2 border-white"
              ></div>
            </div>
            <div class="flex-1 min-w-0">
              <div class="font-medium text-gray-800 truncate">
                {{ user.name }}
              </div>
              <div class="text-xs text-gray-500 truncate">
                <span v-if="user.unread" class="font-bold text-purple-600 mr-1"
                  >•</span
                >
                {{ user.lastMessage }}
              </div>
            </div>
            <div class="text-xs text-gray-400 ml-2 whitespace-nowrap">
              {{ formatShortTime(user.lastMessageTime) }}
            </div>
          </div>
        </div>

        <!-- Chat Area -->
        <div class="w-2/3 flex flex-col">
          <!-- Chat Header -->
          <div
            v-if="activeUser"
            class="p-3 border-b border-gray-200/50 flex items-center bg-white/70"
          >
            <div class="relative mr-3">
              <div
                class="h-8 w-8 rounded-full bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center text-purple-600 font-semibold"
              >
                {{ activeUser.name.charAt(0) }}
              </div>
              <div
                v-if="activeUser.isOnline"
                class="absolute bottom-0 right-0 h-2.5 w-2.5 bg-green-500 rounded-full border-2 border-white"
              ></div>
            </div>
            <div class="flex-1">
              <div class="font-semibold text-gray-800">
                {{ activeUser.name }}
              </div>
              <div class="text-xs text-gray-500">
                <span v-if="activeUser.isOnline" class="text-green-500"
                  >Online</span
                >
                <!-- <span v-else>Last seen {{ formatLastSeen(activeUser.lastSeen) }}</span> -->
              </div>
            </div>
            <!-- <button class="text-gray-400 hover:text-gray-600 p-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z"
                />
              </svg>
            </button> -->
          </div>

          <!-- Messages -->
          <div
            ref="messagesContainer"
            class="flex-1 p-4 overflow-y-auto bg-gradient-to-b from-white to-purple-50/30"
            :class="{ 'flex items-center justify-center': !activeUser }"
          >
            <template v-if="activeUser">
              <div
                class="text-center my-3"
                v-if="isNewDay(chatMessages[0]?.time)"
              >
                <span
                  class="text-xs bg-white px-2 py-1 rounded-full shadow-sm text-gray-500 border border-gray-200/50"
                >
                  {{ formatDate(chatMessages[0].time) }}
                </span>
              </div>

              <div
                v-for="(msg, index) in chatMessages"
                :key="index"
                class="mb-4"
              >
                <!-- New day separator -->
                <div
                  class="text-center my-3"
                  v-if="
                    index > 0 &&
                    isNewDay(msg.time, chatMessages[index - 1].time)
                  "
                >
                  <span
                    class="text-xs bg-white px-2 py-1 rounded-full shadow-sm text-gray-500 border border-gray-200/50"
                  >
                    {{ formatDate(msg.time) }}
                  </span>
                </div>

                <!-- Message bubbles -->
                <div v-if="msg.sender === $t('chat.me')" class="flex justify-end">
                  <div
                    class="max-w-xs lg:max-w-md bg-gradient-to-br from-purple-600 to-indigo-600 text-white rounded-2xl p-3 rounded-tr-none shadow-sm"
                  >
                    <div class="text-sm">{{ msg.text }}</div>
                    <div
                      class="text-right text-xs text-purple-200/80 mt-1 flex items-center justify-end space-x-1"
                    >
                      <span>{{ formatTime(msg.time) }}</span>
                      <svg
                        v-if="msg.status === 'read'"
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-3 w-3 text-blue-300"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
                <div v-else class="flex justify-start">
                  <div
                    class="max-w-xs lg:max-w-md bg-white border border-gray-200/70 rounded-2xl p-3 rounded-tl-none shadow-sm"
                  >
                    <div class="text-sm text-gray-800">{{ msg.text }}</div>
                    <div class="text-xs text-gray-500 mt-1">
                      {{ formatTime(msg.time) }}
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <div v-else class="text-center px-6">
              <div
                class="bg-gradient-to-br from-purple-100 to-indigo-100/50 h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-8 w-8 text-purple-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.5"
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </div>
              <h4 class="font-medium text-gray-700 mb-1">
                {{ $t("chat.selectConversation") }}
              </h4>
              <p class="text-sm text-gray-500">
                {{ $t("chat.chooseContact") }}
              </p>
            </div>
          </div>

          <!-- Message Input -->
          <div
            v-if="activeUser"
            class="p-3 border-t border-gray-200/50 bg-white/80 max-size-input"
          >
            <form
              @submit.prevent="sendMessage"
              class="flex items-center space-x-2"
            >
              <!-- <button
                type="button"
                class="text-gray-400 hover:text-purple-600 p-2"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                  />
                </svg>
              </button> -->
              <input
                v-model="newMessage"
                type="text"
                :placeholder="$t('placeholders.typeMessage')"
                class="max-size-input flex-1 px-4 py-2 rounded-full border border-gray-300/50 focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500/50 bg-white/70"
                @keyup.enter="sendMessage"
              />
              <button
                type="submit"
                class="bg-gradient-to-br from-purple-600 to-indigo-600 text-white p-2 rounded-full hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="!newMessage.trim()"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppNavbar from "./components/AppNavbar.vue";
import FooterComponent from "./components/FooterComponent.vue";
import eventBus from "@/eventBus";
import useAuth from "@/composables/useAuth";
export default {
  name: "App",
  components: {
    AppNavbar,
    FooterComponent,
  },
  setup() {
    const { isAuthenticated } = useAuth();

    return {
      isAuthenticated,
    };
  },
  data() {
    return {
      isChatOpen: false,
      searchQuery: "",
      users: [],
      activeUser: null,
      chatMessages: [],
      newMessage: "",
      unreadCount: 4,
    };
  },
  computed: {
    filteredUsers() {
      return this.users.filter((user) =>
        user.name.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    },
  },
  methods: {
    handleChatClick() {
  if (!this.isAuthenticated) {
    this.$router.push({ name: 'LoginRegisterPage', query: { redirect: this.$route.fullPath } });
  } else {
    this.isChatOpen = !this.isChatOpen;
  }
},
    fetchChatUsers() {
      const token = localStorage.getItem("access_token");
      if (!token) return;

      fetch("https://admin.from-malk.com/api/chat-users", {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => res.json())
        .then((users) => {
          this.users = users.map((user) => ({
            ...user,
            unread: 0,
            lastMessage: "",
            lastMessageTime: null,
            isOnline: false,
            lastSeen: new Date(),
          }));
        })
        .catch(console.error);
    },
    sendMessageAuto(text) {
      if (!this.activeUser) return;

      const newMsg = {
        sender: "Me",
        text: text,
        time: new Date(),
        status: "sent",
      };

      this.chatMessages.push(newMsg);
      this.activeUser.lastMessage = text;
      this.activeUser.lastMessageTime = new Date();
      this.scrollToBottom();

      this.sendMessageToAPI(this.activeUser.id, text);
    },
    selectUser(user) {
      this.activeUser = user;
      user.unread = 0;
      this.unreadCount = Math.max(0, this.unreadCount - (user.unread || 0));
      this.chatMessages = [];

      const token = localStorage.getItem("access_token");

      fetch(`https://admin.from-malk.com/api/chat/${user.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then((res) => res.json())
        .then((messages) => {
          this.chatMessages = messages.map((m) => ({
            sender:
              m.sender_id === parseInt(localStorage.getItem("userId"))
                ? "Me"
                : user.name,
            text: m.text,
            time: new Date(m.created_at),
            status: "read",
          }));
          this.$nextTick(this.scrollToBottom);
        })
        .catch((err) => {
          console.error("Failed to fetch messages", err);
        });
    },
    sendMessage() {
      if (!this.newMessage.trim()) return;

      const newMsg = {
        sender: "Me",
        text: this.newMessage,
        time: new Date(),
        status: "sent",
      };

      this.chatMessages.push(newMsg);
      this.activeUser.lastMessage = this.newMessage;
      this.activeUser.lastMessageTime = new Date();
      const msgToSend = this.newMessage;
      this.newMessage = "";
      this.scrollToBottom();

      // Send to Laravel API
      this.sendMessageToAPI(this.activeUser.id, msgToSend);
    },
    sendMessageToAPI(receiverId, text) {
      const token = localStorage.getItem("access_token");
      fetch("https://admin.from-malk.com/api/send-message", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          receiver_id: receiverId,
          message: text,
        }),
      })
        .then((res) => res.json())
        .then((data) => {
          console.log("Response:", data);
        })
        .catch((err) => console.error("Message send error:", err));
    },

    scrollToBottom() {
      if (this.$refs.messagesContainer) {
        this.$refs.messagesContainer.scrollTop =
          this.$refs.messagesContainer.scrollHeight;
      }
    },
    formatTime(date) {
      return new Date(date).toLocaleTimeString(this.$i18n.locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    },
    formatShortTime(date) {
      if (!date) return "";

      const now = new Date();
      const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return this.formatTime(date);
      } else if (diffDays === 1) {
        return "Yesterday";
      } else if (diffDays < 7) {
        return date.toLocaleDateString([], { weekday: "short" });
      } else {
        return date.toLocaleDateString([], { month: "short", day: "numeric" });
      }
    },
    formatLastSeen(date) {
      const now = new Date();
      const diffMinutes = Math.floor((now - date) / (1000 * 60));

      if (diffMinutes < 1) return "just now";
      if (diffMinutes < 60) return `${diffMinutes}m ago`;
      if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
      return `${Math.floor(diffMinutes / 1440)}d ago`;
    },
  formatDate(date) {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const targetDate = new Date(date);

    if (targetDate.toDateString() === today.toDateString()) {
      return this.$t('dates.today');
    } else if (targetDate.toDateString() === yesterday.toDateString()) {
      return this.$t('dates.yesterday');
    } else {
      return targetDate.toLocaleDateString(this.$i18n.locale, {
        weekday: "long",
        month: "short",
        day: "numeric",
      });
    }
  },
    isNewDay(currentMsgDate, previousMsgDate = null) {
      if (!currentMsgDate) return false;
      if (!previousMsgDate) return true;

      return currentMsgDate.toDateString() !== previousMsgDate.toDateString();
    },
    getRandomReply() {
      const replies = [
        "Thanks for your message!",
        "I'll check that for you right away",
        "Can you provide more details about the issue?",
        "Let me transfer you to a specialist",
        "I've noted your concern and will follow up",
        "That's a great question! Let me find out",
        "I understand your frustration. Let me help",
        "We appreciate your patience",
      ];
      return replies[Math.floor(Math.random() * replies.length)];
    },
  },
  watch: {
    isAuthenticated(newVal) {
      if (newVal) {
        this.fetchChatUsers();
      }
    },
    "$i18n.locale"(newLocale) {
      document.documentElement.dir = newLocale === "ar" ? "rtl" : "ltr";
      document.documentElement.lang = newLocale;
    },
  },

  mounted() {
    const token = localStorage.getItem("access_token");
    const userId = parseInt(localStorage.getItem("userId"));
    eventBus.on("open-chat", ({ userId, product }) => {
      const user = this.users.find((u) => u.id === userId);
      const currentLang = this.$i18n.locale;
      document.documentElement.dir = currentLang === "ar" ? "rtl" : "ltr";
      document.documentElement.lang = currentLang;
      if (user) {
        this.isChatOpen = true;
        this.$nextTick(() => {
          this.selectUser(user);

          setTimeout(() => {
            const productMessage = `🛒 *${product.name}* is available for $${product.price}.\nCheck it here: ${window.location.origin}/product/${product.id}`;
            this.sendMessageAuto(productMessage);
          }, 600);
        });
      } else {
        console.warn("User not found in chat list.");
      }
    });

    console.log("🚀 App.vue mounted and ready");

    if (token && window.Echo) {
      console.log("✅ Echo initialized");
      console.log("⏳ Subscribing to: chat." + userId);

      window.Echo.private(`chat.${userId}`).listen(".message.sent", (e) => {
        console.log("📥 New incoming message:", e.message);

        const isSender = e.message.sender_id === userId;

        const senderUser = this.users.find((u) => u.id === e.message.sender_id);
        const msg = {
          sender: isSender ? "Me" : senderUser?.name || "Unknown",
          text: e.message.text,
          time: new Date(e.message.created_at),
          status: "read",
        };

        if (
          !isSender &&
          this.activeUser &&
          this.activeUser.id === e.message.sender_id
        ) {
          this.chatMessages.push(msg);
          this.scrollToBottom();

          if (senderUser) {
            senderUser.lastMessage = e.message.text;
            senderUser.lastMessageTime = new Date(e.message.created_at);
          }
        } else if (!isSender) {
          if (senderUser) {
            senderUser.unread = (senderUser.unread || 0) + 1;
            this.unreadCount += 1;
            senderUser.lastMessage = e.message.text;
            senderUser.lastMessageTime = new Date(e.message.created_at);
          }
        }
      });
    } else {
      console.warn("❌ Echo not available or token missing");
    }
    fetch("https://admin.from-malk.com/api/chat-users", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((users) => {
        console.log("Loaded chat users:", users);
        this.users = users.map((user) => ({
          ...user,
          unread: 0,
          lastMessage: "",
          lastMessageTime: null,
          isOnline: false,
          lastSeen: new Date(),
        }));
      })
      .catch((err) => {
        console.error("Failed to load users:", err);
      });
  },
};
</script>

<style>
/* Smooth scrolling for messages */
.messages-container {
  scroll-behavior: smooth;
}
@media screen and (max-width: 440px) {
  input, textarea {
    font-size: 16px;
  }
  .max-size{
    max-width:350px;
  }
  .max-size-input{
    max-width:165px;
  }
  .displayNone{
    display: none;
  }
}
</style>
